import { useCallback, useContext, useEffect, useState } from 'react';
import _ from 'lodash';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { BasicSelect, FormCardLayout } from '@main/components';
import Button from '@mui/material/Button';
import { showMessage } from 'app/store/core/messageSlice';
import { useRequest } from '@main/hooks';
import { availableMongoConnections, createMongoDbService } from 'src/app/api/setup';
import { delayFunction } from 'src/app/utils/timer';
import { useSimpleAlert } from '@main/hooks/useSimpleAlert';
import { arraysContainSameValues, convertObjToPickerMono } from 'src/app/utils/conversors';
import { Divider } from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import { showConnectionMongodb } from 'app/store/modals';
import { ProgressStep } from '../Components';
import { StepperContext } from '../Context/SetupContext';
import { checkpointsDatabases, getCompletedCheckpoints } from '../Checkpoints/checkpoints';

const databaseSchema = yup.object().shape({
  mongoMode: yup.string().required('El campo no puede quedar vacío'),
});

const defaultValues = {
  mongoMode: '',
};

export const DataBases = ({ onSuccess = () => {} }) => {
  const Dispatch = useDispatch();
  const openAlert = useSimpleAlert();
  const { addLogs, companyId, currentCheckpoint } = useContext(StepperContext);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [mongoModesList, setMongoModesList] = useState();
  const [disableButton, setDisableButton] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [mongoMode, setMongoMode] = useState(undefined);

  // ----- Inicializacion de validadores de formulario
  const { control, formState, handleSubmit, watch } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(databaseSchema),
  });
  const { isValid, errors } = formState;
  const watchConnectionType = watch('mongoMode');

  // -------- Obteniendo checkpoints
  const handleUpdateSteps = (incomingStep) => {
    const stepIsThere = completedSteps.includes(incomingStep);
    if (stepIsThere) return;
    setCompletedSteps((current) => [...current, incomingStep]);
  };

  useEffect(() => {
    if (currentCheckpoint === undefined || currentCheckpoint === null) return;
    const isThere = checkpointsDatabases.includes(currentCheckpoint);
    if (!isThere) return;
    console.log(currentCheckpoint);
    const checkpointsList = getCompletedCheckpoints(currentCheckpoint);
    if (checkpointsList !== undefined) {
      console.log(checkpointsList);
      setCompletedSteps(checkpointsList);
    }
  }, [currentCheckpoint]);

  useEffect(() => {
    console.log('completed steps', completedSteps);
    if (completedSteps === undefined) return;
    console.log('has changed completedSteps');
    const isStepsComplete = arraysContainSameValues(completedSteps, checkpointsDatabases);
    console.log('Esta completo? ', isStepsComplete);
    setIsCompleted(isStepsComplete);
  }, [completedSteps]);

  useEffect(() => {
    if (isCompleted === undefined) return;
    onSuccess(isCompleted);
  }, [isCompleted]);

  useEffect(() => {
    return () => {
      setCompletedSteps([]);
    };
  }, []);

  const requestOptions = {
    onLog: addLogs,
  };

  const {
    makeRequest: tryCreateMongoDB,
    isLoading: isLoadingCreateMongoDB,
    status: statusCreateMongoDB,
  } = useRequest(createMongoDbService, requestOptions);

  const { makeRequest: tryGetMongoOptions, response: mongoOptionsRes } =
    useRequest(availableMongoConnections);

  const handleExtractMongoKeys = (mongoObj) => {
    const contentList = convertObjToPickerMono(mongoObj);
    setMongoModesList(contentList);
  };

  useEffect(() => {
    tryGetMongoOptions();
  }, []);

  useEffect(() => {
    if (mongoOptionsRes === undefined) return;
    console.log(mongoOptionsRes);
    handleExtractMongoKeys(mongoOptionsRes);
  }, [mongoOptionsRes]);

  const tryStepsWizard = async (currentForm) => {
    setMongoMode(currentForm?.mongoMode);
    setDisableButton(true);
    if (_.isEmpty(currentForm) || currentForm === undefined) {
      setDisableButton(false);
      Dispatch(
        showMessage({
          message: 'Formulario no valido, verificar datos ingresados',
        })
      );
      return;
    }
    if (companyId === undefined) {
      Dispatch(
        showMessage({
          message: 'No se encontro el ID de compania',
        })
      );
      setDisableButton(false);
      return;
    }
    try {
      // ---- Instalar MongoDB
      const step1ok = await delayFunction(
        completedSteps.includes(checkpointsDatabases[0]),
        tryCreateMongoDB,
        companyId,
        { connection: currentForm?.mongoMode }
      );
      if (!step1ok) return;
      console.log('Step okii');
      handleUpdateSteps(checkpointsDatabases[0]);
    } catch (error) {
      console.log(error);
    } finally {
      setDisableButton(false);
    }
  };

  const handleBypassStep = (stepName) => {
    if (typeof stepName !== 'string' || stepName === undefined) return;
    openAlert({
      title: 'Atencion',
      description: 'Esta seguro que desea omitir este paso?',
    }).then((userResponseOk) => {
      if (!userResponseOk) return;
      handleUpdateSteps(stepName);
      Dispatch(
        showMessage({
          message: `El paso ${stepName} ha sido omitido`,
        })
      );
    });
  };

  useEffect(() => {
    console.log(companyId);
  }, [companyId]);

  const handleManualRetry = (reqToRetry = () => new Promise(), ...args) => {
    reqToRetry(...args)
      .then((isOk) => {
        console.log(isOk);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleViewModal = useCallback(() => {
    Dispatch(showConnectionMongodb(watchConnectionType, mongoOptionsRes));
  }, [watchConnectionType, mongoOptionsRes]);

  return (
    <>
      <FormCardLayout.FullColum>
        <div className="mt-24 flex flex-row w-full justify-center items-center">
          <form
            name="mongoModeForm"
            noValidate
            className="flex flex-row justify-around items-center w-full"
            autoComplete="off"
          >
            <div className="py-16">
              <Divider sx={{ fontSize: '1.8rem' }}>Configuracion de conexion de MongoDB</Divider>
            </div>
            <div className="flex gap-10 items-center justify-center">
              <Controller
                name="mongoMode"
                control={control}
                render={({ field }) => (
                  <BasicSelect
                    disabled={isCompleted}
                    value={field.value}
                    data={mongoModesList}
                    title="Tipo de conexion"
                    error={!!errors.mongoMode}
                    required
                    staticWidth="24rem"
                    onChange={(value) => {
                      field.onChange(value);
                    }}
                  />
                )}
              />
              <Button
                onClick={handleViewModal}
                disabled={!watchConnectionType || watchConnectionType.length === 0}
                variant="text"
              >
                <HelpIcon
                  sx={{
                    color:
                      !watchConnectionType || watchConnectionType.length === 0
                        ? 'grey'
                        : 'primary.main',
                  }}
                />
              </Button>
            </div>
            <Button
              aria-label="Comenzar"
              onClick={handleSubmit(tryStepsWizard)}
              variant="contained"
              className="w-full md:w-auto"
              color="primary"
              disabled={!isValid || disableButton || isCompleted}
            >
              Comenzar
            </Button>
          </form>
        </div>
      </FormCardLayout.FullColum>
      <FormCardLayout.FullColum>
        <Divider variant="middle" />
      </FormCardLayout.FullColum>
      <FormCardLayout.FullColum>
        <div className="px-32 flex flex-col w-full justify-start items-start">
          <ProgressStep
            onRetry={() => tryStepsWizard()}
            onBypass={() => handleBypassStep(checkpointsDatabases[0])}
            status={
              completedSteps.includes(checkpointsDatabases[0]) ? 'SUCCESS' : statusCreateMongoDB
            }
            isLoading={isLoadingCreateMongoDB}
            stepName="Ambientando MongoDB"
            disableOptions={completedSteps.includes(checkpointsDatabases[0])}
          />
        </div>
      </FormCardLayout.FullColum>
    </>
  );
};
